import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { toNumber } from '@/lib/format';
import { getStatusMeta } from '@/lib/status';
import { StatusPill } from '@/components/ui/status-pill';

/*
  AI answers as formatted text (25 idea 1). A small, safe Markdown reader — no HTML is ever rendered:
  headings, paragraphs, '-' / '1.' lists (nested), tables, **bold**, *italic*, `code`, links (http(s) and
  in-app paths only). Tables get the app's number style: money / count columns right-aligned with
  tabular figures, an "Age" column coloured (over 180 d red, 120–180 d amber), statuses as pills, and a
  "Total" / "Top 5" row on the grey band. Project names become links to the project when the server
  says which project they are (`projectRefs` = { "City Walk": "<project id>" }).
*/

/* ------------------------------------------------------------------ parsing */

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const LIST_ITEM = /^(\s*)([-*•+]|\d{1,3}[.)])\s+(.*)$/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;

export function splitCells(line) {
  const cells = [];
  let current = '';
  const text = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '\\' && text[index + 1] === '|') {
      current += '|';
      index += 1;
    } else if (char === '|') {
      cells.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
}

const indentOf = (raw) => raw.replace(/\t/g, '    ').length;

/** Markdown text → blocks: { type: 'heading'|'paragraph'|'list'|'table'|'rule', … }. */
export function parseMarkdown(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let paragraph = [];
  const flush = () => {
    if (paragraph.length) blocks.push({ type: 'paragraph', lines: paragraph });
    paragraph = [];
  };
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      flush();
      index += 1;
    } else if (TABLE_ROW.test(line) && index + 1 < lines.length && TABLE_SEPARATOR.test(lines[index + 1]) && lines[index + 1].includes('-')) {
      flush();
      const headers = splitCells(line);
      const align = splitCells(lines[index + 1]).map((cell) => (/^:-+:$/.test(cell) ? 'center' : /-:$/.test(cell) ? 'right' : ''));
      const rows = [];
      index += 2;
      while (index < lines.length && TABLE_ROW.test(lines[index])) {
        rows.push(splitCells(lines[index]));
        index += 1;
      }
      blocks.push({ type: 'table', headers, align, rows });
    } else if (HEADING.test(line)) {
      flush();
      const [, hashes, content] = HEADING.exec(line);
      blocks.push({ type: 'heading', level: hashes.length, text: content });
      index += 1;
    } else if (RULE.test(line)) {
      flush();
      blocks.push({ type: 'rule' });
      index += 1;
    } else if (LIST_ITEM.test(line)) {
      flush();
      // A run of list lines (and their indented continuation lines) → one nested list.
      const items = [];
      while (index < lines.length) {
        const current = lines[index];
        const match = LIST_ITEM.exec(current);
        // A top-level item of the other kind ('-' after '1.') starts a new list, as in Markdown.
        if (match && items.length && indentOf(match[1]) <= items[0].indent && /\d/.test(match[2]) !== items[0].ordered) break;
        if (match) {
          items.push({ indent: indentOf(match[1]), ordered: /\d/.test(match[2]), number: parseInt(match[2], 10) || null, text: match[3] });
          index += 1;
        } else if (current.trim() && /^\s{2,}/.test(current) && items.length) {
          items[items.length - 1].text += ` ${current.trim()}`;
          index += 1;
        } else if (!current.trim() && index + 1 < lines.length && LIST_ITEM.test(lines[index + 1]) && /^\s+/.test(lines[index + 1])) {
          index += 1; // blank line inside a nested list
        } else {
          break;
        }
      }
      blocks.push({ type: 'list', tree: buildTree(items) });
    } else {
      paragraph.push(line.trim());
      index += 1;
    }
  }
  flush();
  return blocks;
}

function buildTree(items) {
  const root = { children: [], ordered: items[0]?.ordered, indent: -1 };
  const stack = [root];
  items.forEach((item) => {
    while (stack.length > 1 && item.indent <= stack[stack.length - 1].indent) stack.pop();
    const parent = stack[stack.length - 1];
    const node = { ...item, children: [] };
    if (!parent.children.length) parent.childOrdered = item.ordered;
    parent.children.push(node);
    stack.push(node);
  });
  return root;
}

/* ------------------------------------------------------------------ inline */

// No lookbehind in here: Safari / iOS before 16.4 cannot even parse one (Babel cannot transpile it), and
// this module is in the main bundle, so a lookbehind would blank the whole app on older iPhones and iPads.
// "_italic_" must not start inside a word (snake_case_name): renderInline checks the character before it.
const INLINE = /(\\[\\`*_{}[\]()#+\-.!|>~])|(\*\*([^*]+?)\*\*)|(__([^_]+?)__)|(`([^`\n]+)`)|(\[([^\]\n]+)\]\(([^)\s]+)\))|(\*(?!\s)([^*\n]+?)\*)|(_(?!\s)([^_\n]+?)_(?![A-Za-z0-9]))/g;
const WORD_CHAR = /[A-Za-z0-9]/;

const refKey = (value) => String(value || '').trim().toLowerCase().replace(/\*\*/g, '');

function useRefs(projectRefs) {
  const map = new Map();
  Object.entries(projectRefs || {}).forEach(([name, id]) => { if (id) map.set(refKey(name), { name, id }); });
  return map;
}

function ProjectLink({ id, children }) {
  return (
    <Link to={`/projects/${id}`} className="font-semibold text-brand-ink underline-offset-2 hover:underline">
      {children}
    </Link>
  );
}

function SafeLink({ href, children }) {
  if (/^\/(?!\/)/.test(href)) {
    return <Link to={href} className="font-semibold text-brand-ink underline-offset-2 hover:underline">{children}</Link>;
  }
  if (/^https?:\/\//i.test(href)) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-ink underline-offset-2 hover:underline">{children}</a>;
  }
  return <>{children}</>;
}

export function renderInline(text, refs, keyPrefix = 'i') {
  const source = String(text ?? '');
  const out = [];
  let last = 0;
  let count = 0;
  // A fresh pattern per call: renderInline is recursive (bold inside a link…), a shared /g regex would
  // have its lastIndex reset by the inner call and loop for ever.
  const pattern = new RegExp(INLINE.source, 'g');
  let match = pattern.exec(source);
  while (match) {
    if (match[13] && match.index > 0 && WORD_CHAR.test(source.charAt(match.index - 1))) {
      // An "_" inside a word is not italic: it stays text, and the search goes on right after it.
      pattern.lastIndex = match.index + 1;
      match = pattern.exec(source);
      continue;
    }
    if (match.index > last) out.push(source.slice(last, match.index));
    const key = `${keyPrefix}-${count += 1}`;
    if (match[1]) {
      out.push(match[1].slice(1));
    } else if (match[2] || match[4]) {
      const inner = match[3] ?? match[5];
      const ref = refs?.get(refKey(inner));
      out.push(ref
        ? <ProjectLink key={key} id={ref.id}><strong className="font-bold">{inner}</strong></ProjectLink>
        : <strong key={key} className="font-bold text-ink">{renderInline(inner, refs, key)}</strong>);
    } else if (match[6]) {
      out.push(<code key={key} className="rounded bg-surface-2 px-1 py-px font-mono text-[12px] text-ink">{match[7]}</code>);
    } else if (match[8]) {
      out.push(<SafeLink key={key} href={match[10]}>{renderInline(match[9], refs, key)}</SafeLink>);
    } else if (match[11]) {
      out.push(<em key={key}>{renderInline(match[12], refs, key)}</em>);
    } else if (match[13]) {
      out.push(<em key={key}>{renderInline(match[14], refs, key)}</em>);
    }
    last = match.index + match[0].length;
    match = pattern.exec(source);
  }
  if (last < source.length) out.push(source.slice(last));
  return out;
}

/** "Expo Dubai (Active) | ATS" → the leading project name as a link, the rest as text. */
function withLeadingProject(text, refs, keyPrefix) {
  if (refs?.size) {
    const plain = String(text || '');
    const names = [...refs.values()].sort((a, b) => b.name.length - a.name.length);
    const hit = names.find(({ name }) => {
      const head = plain.slice(0, name.length);
      const next = plain.charAt(name.length);
      return head.toLowerCase() === name.toLowerCase() && (!next || /[\s(|:,–—-]/.test(next));
    });
    if (hit) {
      return [
        <ProjectLink key={`${keyPrefix}-p`} id={hit.id}>{plain.slice(0, hit.name.length)}</ProjectLink>,
        ...renderInline(plain.slice(hit.name.length), refs, keyPrefix),
      ];
    }
  }
  return renderInline(text, refs, keyPrefix);
}

/* ------------------------------------------------------------------ tables */

const MONEY_HEADER = /amount|aed|value|price|cost|total|balance|sum|paid|due \(/i;
const NUMBER_HEADER = /qty|quantity|count|number of|no\.|%|percent|days?\b|share/i;
const AGE_HEADER = /^(age|days?( late| old| overdue| open)?|overdue|late( by)?)\b/i;
const STATUS_HEADER = /status|state/i;
const PROJECT_HEADER = /project/i;
const TOTAL_ROW = /^(total|grand total|sum|subtotal|top \d+|all \d+)\b/i;

const plainCell = (cell) => String(cell || '').replace(/\*\*|__|`/g, '').trim();

/** The number in "210 d", "210 days", "AED 94,605.00", "22%". */
export function cellNumber(cell) {
  const text = plainCell(cell).replace(/\s*(d|days?|%)$/i, '').replace(/^aed\s*/i, '');
  return toNumber(text);
}

/** Days → tone: over 180 red, 120–180 amber (the shared age rule of the AI answers). */
export function ageTone(days) {
  if (days === null || days === undefined || !Number.isFinite(days)) return '';
  if (days > 180) return 'red';
  if (days >= 120) return 'amber';
  return '';
}

export function describeColumns(table) {
  return table.headers.map((header, column) => {
    const values = table.rows.map((row) => plainCell(row[column])).filter(Boolean);
    const numericShare = values.length ? values.filter((value) => cellNumber(value) !== null).length / values.length : 0;
    const age = AGE_HEADER.test(plainCell(header));
    const money = MONEY_HEADER.test(header) && numericShare >= 0.5;
    const numeric = age || money || numericShare >= 0.8 || (NUMBER_HEADER.test(header) && numericShare >= 0.5);
    return {
      numeric,
      money,
      age,
      status: STATUS_HEADER.test(header),
      project: PROJECT_HEADER.test(header),
      align: table.align?.[column] || (numeric ? 'right' : ''),
    };
  });
}

function TableCell({ cell, spec, refs, keyPrefix, total }) {
  const text = plainCell(cell);
  if (!text) return total ? null : <span className="text-ink-4" aria-label="None">—</span>;
  const ref = refs?.get(refKey(text));
  if (ref) return <ProjectLink id={ref.id}>{text}</ProjectLink>;
  if (spec.age) {
    const tone = ageTone(cellNumber(text));
    return (
      <span className={cn('font-semibold', tone === 'red' && 'text-status-red-ink', tone === 'amber' && 'text-status-amber-ink')}>
        {text}
      </span>
    );
  }
  if (spec.status) {
    const meta = getStatusMeta(text);
    if (meta.known) return <StatusPill status={text} size="sm" />;
  }
  return <>{renderInline(cell, refs, keyPrefix)}</>;
}

function AnswerTable({ table, refs, keyPrefix }) {
  const columns = describeColumns(table);
  return (
    <div className="my-3 overflow-x-auto rounded-[10px] border border-line" data-testid="ai-answer-table">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="bg-surface-2">
            {table.headers.map((header, column) => (
              <th
                // eslint-disable-next-line react/no-array-index-key
                key={column}
                scope="col"
                className={cn(
                  'h-8 whitespace-nowrap border-b border-line px-3 text-left text-[11.5px] font-semibold text-ink-3',
                  columns[column].align === 'right' && 'text-right',
                  columns[column].align === 'center' && 'text-center'
                )}
              >
                {plainCell(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => {
            const total = TOTAL_ROW.test(plainCell(row[0]));
            return (
              <tr
                // eslint-disable-next-line react/no-array-index-key
                key={rowIndex}
                className={cn(total ? 'bg-surface-2 font-semibold' : 'border-t border-line-2 first:border-t-0')}
                data-total={total ? 'true' : undefined}
              >
                {table.headers.map((_, column) => {
                  const spec = columns[column];
                  return (
                    <td
                      // eslint-disable-next-line react/no-array-index-key
                      key={column}
                      className={cn(
                        'h-9 px-3 py-1.5 align-middle text-ink',
                        total && 'border-t border-line',
                        spec.align === 'right' && 'whitespace-nowrap text-right tabular-nums',
                        spec.align === 'center' && 'text-center',
                        spec.money && 'font-bold'
                      )}
                    >
                      <TableCell cell={row[column]} spec={spec} refs={refs} total={total} keyPrefix={`${keyPrefix}-${rowIndex}-${column}`} />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ lists */

function ListNodes({ node, refs, keyPrefix, depth = 0 }) {
  const Tag = node.childOrdered ? 'ol' : 'ul';
  return (
    <Tag
      className={cn(
        'space-y-1',
        depth === 0 ? 'my-2' : 'mt-1',
        node.childOrdered ? 'list-decimal pl-6 marker:font-semibold marker:text-ink-3' : 'list-none pl-4'
      )}
      start={node.childOrdered && node.children[0]?.number > 1 ? node.children[0].number : undefined}
    >
      {node.children.map((child, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index} className={cn('pl-1 leading-[1.55]', !node.childOrdered && 'relative before:absolute before:-left-3 before:top-[0.62em] before:h-[5px] before:w-[5px] before:rounded-full before:bg-[color:var(--ink-4)]')}>
          {withLeadingProject(child.text, refs, `${keyPrefix}-${index}`)}
          {child.children.length ? <ListNodes node={child} refs={refs} keyPrefix={`${keyPrefix}-${index}`} depth={depth + 1} /> : null}
        </li>
      ))}
    </Tag>
  );
}

/* ------------------------------------------------------------------ component */

const HEADING_CLASS = {
  1: 'mt-3 text-[15.5px] font-bold text-ink first:mt-0',
  2: 'mt-3 text-[15px] font-bold text-ink first:mt-0',
  3: 'mt-3 text-[14px] font-bold text-ink first:mt-0',
};

export default function AiMarkdown({ text, projectRefs, className }) {
  const refs = useRefs(projectRefs);
  const blocks = parseMarkdown(text);
  return (
    <div className={cn('break-words text-[13.5px] leading-[1.55] text-ink', className)} data-testid="ai-markdown">
      {blocks.map((block, index) => {
        const key = `b${index}`;
        if (block.type === 'heading') {
          const level = Math.min(block.level, 3);
          return <p key={key} role="heading" aria-level={level + 2} className={HEADING_CLASS[level]}>{renderInline(block.text, refs, key)}</p>;
        }
        if (block.type === 'table') return <AnswerTable key={key} table={block} refs={refs} keyPrefix={key} />;
        if (block.type === 'list') return <ListNodes key={key} node={block.tree} refs={refs} keyPrefix={key} />;
        if (block.type === 'rule') return <hr key={key} className="my-3 border-line" />;
        return (
          <p key={key} className="my-2 first:mt-0 last:mb-0">
            {block.lines.map((line, lineIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={lineIndex}>
                {lineIndex ? <br /> : null}
                {renderInline(line, refs, `${key}-${lineIndex}`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
